// ** MUI Imports
import { useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import MuiSwipeableDrawer, { SwipeableDrawerProps } from '@mui/material/SwipeableDrawer'
import { Fragment } from 'react'

// ** Type Import
import { LayoutProps } from 'src/@core/layouts/types'

interface Props {
  navWidth: number
  navHover: boolean
  navVisible: boolean
  collapsedNavWidth: number
  hidden: LayoutProps['hidden']
  navigationBorderWidth: number
  settings: LayoutProps['settings']
  children: LayoutProps['children']
  setNavHover: (values: boolean) => void
  setNavVisible: (value: boolean) => void
  navMenuProps: LayoutProps['verticalLayoutProps']['navMenu']['componentProps']
  saveSettings: LayoutProps['saveSettings']
}

const SwipeableDrawer = styled(MuiSwipeableDrawer)<SwipeableDrawerProps>({
  overflowX: 'hidden',
  transition: 'width .25s ease-in-out',
  '& ul': {
    listStyle: 'none'
  },
  '& .MuiListItem-gutters': {
    paddingLeft: 4,
    paddingRight: 4
  },
  '& .MuiDrawer-paper': {
    left: 'unset',
    right: 'unset',
    overflowX: 'hidden',
    transition: 'width .25s ease-in-out, box-shadow .25s ease-in-out'
  }
})

const Drawer = (props: Props) => {
  // ** Props
  const {
    hidden,
    children,
    navHover,
    navWidth,
    settings,
    navVisible,
    navMenuProps,
    collapsedNavWidth,
    navigationBorderWidth,
    setNavVisible
  } = props

  // ** Vars
  const { skin, navCollapsed } = settings

  // Drawer Props for Mobile & Tablet screens
  const MobileDrawerProps = {
    open: navVisible,
    onOpen: () => null,
    onClose: () => setNavVisible(false),
    ModalProps: {
      keepMounted: false // Better open performance on mobile.
    }
  }

  /** Permanent drawer is always visible on desktop; `open` only applies to temporary/mobile. */
  const DesktopDrawerProps = {
    open: true,
    onOpen: () => null,
    onClose: () => null
  }

  let userNavMenuStyle = {}
  let userNavMenuPaperStyle = {}
  if (navMenuProps && navMenuProps.sx) {
    userNavMenuStyle = navMenuProps.sx
  }
  if (navMenuProps && navMenuProps.PaperProps && navMenuProps.PaperProps.sx) {
    userNavMenuPaperStyle = navMenuProps.PaperProps.sx
  }
  const userNavMenuProps = Object.assign({}, navMenuProps)
  delete userNavMenuProps.sx
  delete userNavMenuProps.PaperProps

  const isLandscape = useMediaQuery('(max-width:1200px) and (orientation: landscape)')
  const isPortrait = useMediaQuery('(max-width:1024px) and (orientation: portrait)')

  return (
    <Fragment>
      <SwipeableDrawer
        className='layout-vertical-nav'
        variant={hidden ? 'temporary' : 'permanent'}
        {...(hidden ? { ...MobileDrawerProps } : { ...DesktopDrawerProps })}
        PaperProps={{
          sx: {
            // overflow: 'visible',
            backgroundColor: 'text.primary',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            maxHeight: '100dvh',
            overflow: 'hidden',
            ...(!hidden && skin !== 'bordered' && { boxShadow: 2 }),
            zIndex: theme => theme.zIndex.drawer + 1,
            width: navCollapsed && !navHover && (!isPortrait || !isLandscape) ? collapsedNavWidth : navWidth,
            borderRight: theme =>
              navigationBorderWidth === 0 ? 0 : `${navigationBorderWidth}px solid ${theme.palette.divider}`,
            ...userNavMenuPaperStyle
          },
          ...navMenuProps?.PaperProps
        }}
        sx={{
          width: navCollapsed && (!isPortrait || !isLandscape) ? collapsedNavWidth : navWidth,
          ...userNavMenuStyle
        }}
        {...userNavMenuProps}
      >
        {children}
      </SwipeableDrawer>
    </Fragment>
  )
}

export default Drawer
